'use client';

import React, { useEffect, useRef, useState } from "react";
import Headline from "@/components/ui/Headline/Headline";
import ScrollActivatedNumber from "@/components/ui/ScrollActivatedNumber/ScrollActivatedNumber";
import { LanguageType, LanguageDirection } from "@/lib/types";
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from "@gsap/react";
import PrimaryButton from "@/components/ui/PrimaryButton/PrimaryButton";
import Subheading from "@/components/ui/Subheading/Subheading";
import { useLenis } from "@/contexts/LenisContext";
import Image from "next/image";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface TimelineItem {
    number: string;
    title: string;
    text: string;
    additionalText?: string;
    imageUrl: string;
    imageAlt: string;
}

interface TimelineSectionProps {
    language?: LanguageType;
    headlineText: string;
    subheadingText: string;
    paragraphText?: string;
    timelineItems: TimelineItem[];
    buttonText?: string;
    testimonialText?: string;
    testimonialAuthor?: string;
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
    english: "ltr",
    arabic: "rtl"
};

const TimelineSection: React.FC<TimelineSectionProps> = ({
    language = "english",
    headlineText,
    subheadingText,
    paragraphText,
    timelineItems,
    buttonText = "Join our waitlist",
    testimonialText = "Worth every minute on the waiting list.",
    testimonialAuthor = "SARAH K."
}) => {
    const { lenis } = useLenis();
    const isLg = useIsLg();
    const firstNumberRef = useRef<HTMLDivElement>(null);
    const lastNumberRef = useRef<HTMLDivElement>(null);
    const progressLineRef = useRef<HTMLDivElement>(null);
    const timelineContainerRef = useRef<HTMLDivElement>(null);
    const imageContainerRefs = useRef<(HTMLDivElement | null)[]>([]);
    const imageRefs = useRef<(HTMLImageElement | null)[]>([]);
    const [lineHeight, setLineHeight] = useState<number>(0);

    useEffect(() => {
        const calculateLineHeight = () => {
            if (firstNumberRef.current && lastNumberRef.current) {
                const firstRect = firstNumberRef.current.getBoundingClientRect();
                const lastRect = lastNumberRef.current.getBoundingClientRect();

                // Calculate distance between centers of first and last ShiningNumbers
                const firstCenter = firstRect.top + firstRect.height / 2;
                const lastCenter = lastRect.top + lastRect.height / 2;
                const height = lastCenter - firstCenter;

                setLineHeight(height);
            }
        };

        // Width-only resize guard — skip mobile-chrome height-only changes
        // that fire constantly on scroll
        let lastWidth = window.innerWidth;
        const onResize = () => {
            if (window.innerWidth === lastWidth) return;
            lastWidth = window.innerWidth;
            calculateLineHeight();
        };

        // Calculate on mount and when layout changes
        calculateLineHeight();
        window.addEventListener('resize', onResize);

        // Small delay to ensure layout is settled
        const timeoutId = setTimeout(calculateLineHeight, 100);

        return () => {
            window.removeEventListener('resize', onResize);
            clearTimeout(timeoutId);
        };
    }, [timelineItems, isLg]);

    // GSAP ScrollTrigger for progress line animation
    useGSAP(() => {
        if (progressLineRef.current && timelineContainerRef.current && lineHeight > 0) {
            gsap.fromTo(
                progressLineRef.current,
                {
                    height: 0
                },
                {
                    height: lineHeight,
                    ease: "none",
                    scrollTrigger: {
                        trigger: timelineContainerRef.current,
                        start: "top bottom-=35%", // this has to be in sync with the ScrollActivatedNumber
                        end: `+=${lineHeight}`,
                        // markers: true,
                        scrub: 1,
                    }
                }
            );
        }
    }, { dependencies: [lineHeight, timelineItems], revertOnUpdate: true });

    // Parallax effect for the desktop step images
    useGSAP(() => {
        if (!isLg) return;

        timelineItems.forEach((_, index) => {
            const image = imageRefs.current[index];
            const container = imageContainerRefs.current[index];

            if (!image || !container) return;

            gsap.fromTo(image, {
                yPercent: -10
            }, {
                yPercent: 10,
                ease: "none",
                force3D: true,
                scrollTrigger: {
                    trigger: container,
                    start: "top bottom",
                    end: "bottom top",
                    scrub: true,
                    invalidateOnRefresh: true,
                    id: `timeline-section-image-parallax-${index}`
                },
                onEnter: () => {
                    image.style.willChange = "transform";
                },
                onLeave: () => {
                    image.style.willChange = "auto";
                },
                onEnterBack: () => {
                    image.style.willChange = "transform";
                },
                onLeaveBack: () => {
                    image.style.willChange = "auto";
                }
            });
        });
    }, { dependencies: [isLg, timelineItems], revertOnUpdate: true });

    return (
        <section
            style={{
                direction: LANGUAGE_TO_DIRECTION[language]
            }}
            className="
                w-full flex items-center justify-center
                px-[1.25rem] lg:px-[1.5rem]
            "
            >
            <div className="w-full container-large flex flex-col items-center justify-center">
                {/* Headline */}
                <div className="flex items-center justify-center w-full">
                    <Headline
                        subheading={subheadingText}
                        heading={headlineText}
                        type="long"
                        alignment="center"
                        paragraph={paragraphText}
                        language={language}
                        shouldAnimate={false}
                    />
                </div>

                {/* Timeline Cards */}
                <div ref={timelineContainerRef} className="w-full flex flex-col gap-[2.5rem] lg:gap-[3.5rem] mt-[3.5rem] lg:mt-[7rem]">
                    {timelineItems.map((item, index) => {
                        const isFirstItem = index === 0;
                        const isLastItem = index === timelineItems.length - 1;

                        return (
                            <div
                                key={index}
                                className="
                                    flex flex-row items-start
                                    gap-[1.25rem] lg:gap-[4.5rem]
                                    w-full
                            "
                            >
                                {/* Image - Hidden on mobile */}
                                {isLg && (
                                    <div className="flex-1 flex items-center justify-center">
                                        <div
                                            ref={(el) => { imageContainerRefs.current[index] = el; }}
                                            className="relative w-full overflow-hidden border-radius-main outline-secondary outline-stroke-soft-200"
                                            style={{ aspectRatio: '1.78' }}
                                        >
                                            <Image
                                                ref={(el) => { imageRefs.current[index] = el; }}
                                                src={item.imageUrl}
                                                alt={item.imageAlt}
                                                fill
                                                sizes="(min-width: 992px) 40vw"
                                                loading="eager"
                                                className="object-cover"
                                                style={{ transformOrigin: 'center center', scale: 1.1 }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* ShiningNumber with Continuous Line (first item only) */}
                                <div
                                    className="relative flex items-start justify-center pt-0 lg:pt-[2rem]"
                                    style={{
                                        width: isLg ? '64px' : '44px'
                                    }}
                                >
                                    {/* Continuous Vertical Lines - Only rendered on first item */}
                                    {isFirstItem && lineHeight > 0 && (
                                        <>
                                            {/* Gray background line */}
                                            <div
                                                className="absolute w-[2px] bg-bg-alpha-medium-light/32"
                                                style={{
                                                    zIndex: -2,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    top: '50%',
                                                    height: `${lineHeight}px`,
                                                }}
                                            />
                                            {/* Orange progress line */}
                                            <div
                                                ref={progressLineRef}
                                                className="absolute w-[2px] bg-theme-yellow-primary-base"
                                                style={{
                                                    zIndex: -1,
                                                    left: '50%',
                                                    transform: 'translateX(-50%)',
                                                    top: '50%',
                                                    height: 0,
                                                }}
                                            />
                                        </>
                                    )}

                                    <ScrollActivatedNumber
                                        number={`0${item.number}`}
                                        size={isLg ? 64 : 44}
                                        onRef={(el) => {
                                            if (isFirstItem) firstNumberRef.current = el;
                                            if (isLastItem) lastNumberRef.current = el;
                                        }}
                                    />
                                </div>

                                {/* Text Content */}
                                <div className="flex-1 flex items-start lg:pt-[1.5rem]">
                                    <div
                                        className="flex flex-col gap-[1.25rem] lg:gap-[0.75rem] max-w-[30.6875rem] items-start"
                                        style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
                                    >
                                        <h3 className="text-30-to-20--neue-june text-text-primary lg:pt-[0.5rem] lg:pb-[0.75rem]">
                                            {item.title}
                                        </h3>
                                        <div className="flex flex-col gap-[1rem]">
                                            <p className="text-18--alexandria text-text-tertiary">
                                                {item.text}
                                            </p>
                                            {item.additionalText && (
                                                <p className="text-16-to-12--alexandria text-text-quinary">
                                                    {item.additionalText}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    <div className="w-full flex flex-col items-center justify-center pt-[1.5rem] lg:pt-[2rem] gap-[1.5rem] lg:gap-[2rem]">
                        <div className="w-fit">
                            <PrimaryButton
                              text={buttonText}
                              icon={true}
                              size={isLg ? 64 : 52}
                              type='accent'
                              language={language}
                              onClick={() => {
                                const section = document.getElementById('signup-section');
                                if (section) {
                                  // Emit event to hide navbar during smooth scroll
                                  window.dispatchEvent(new CustomEvent('lenisScrollStart'));

                                  const y = section.getBoundingClientRect().top + window.scrollY;

                                  lenis?.scrollTo(y, {
                                    offset: 0,
                                    duration: 2,
                                    easing: (t: number) => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1,
                                    immediate: false,
                                    lock: true,
                                    onComplete: () => {
                                      // Re-enable user scroll and navbar
                                      window.dispatchEvent(new CustomEvent('lenisScrollEnd'));
                                    }
                                  });
                                }
                              }}
                            />
                        </div>
                        <div 
                        style={{ direction: "ltr" }}
                        className="w-full flex flex-col items-center justify-center gap-[1rem] lg:gap-[1.25rem]">
                            <p className="text-20--alexandria text-text-quaternary text-center" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                                "{testimonialText}"
                            </p>
                            <Subheading
                                size={20}
                                subheading={testimonialAuthor}
                                alignment="center"
                                textColor="text-text-quinary"
                            />
                        </div>

                    </div>
                </div>
            </div>
        </section>
    );
};

export default TimelineSection;
